import type { PlatformAdapter } from '@rudi/api/platform';
import type { AuthUser, AuthStateInternal } from './types';

/** Auth operations returned by createAuthClient */
export interface AuthClient {
  /** Login with username and password */
  login(
    username: string,
    password: string
  ): Promise<{ user: AuthUser; accessToken: string; refreshToken: string }>;
  /** Logout — clear all tokens and state */
  logout(): Promise<void>;
  /** Refresh the access token using the stored refresh token */
  refresh(): Promise<string | null>;
  /** Refresh user profile from /api/auth/status */
  refreshUser(): Promise<AuthUser>;
  /** Verify the current token is still valid */
  verifyToken(): Promise<void>;
  /** Get current internal state snapshot */
  getState(): AuthStateInternal;
}

/**
 * Create a platform-agnostic auth client that manages login, logout, and token refresh.
 *
 * This is the pure-logic layer — no React. The React bindings (AuthProvider, useAuth)
 * wrap this client and expose it via context.
 *
 * @param adapter - PlatformAdapter for token storage, URL resolution, and session expiry handling
 */
export function createAuthClient(adapter: PlatformAdapter): AuthClient {
  // Internal state
  let state: AuthStateInternal = {
    user: null,
    accessToken: null,
    refreshToken: null,
    isLoading: false,
    isAuthenticated: false,
    error: null,
  };

  const apiBaseUrl = adapter.getApiBaseUrl();

  async function login(
    username: string,
    password: string
  ): Promise<{ user: AuthUser; accessToken: string; refreshToken: string }> {
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
    } catch (fetchError) {
      const errorMsg = fetchError instanceof Error ? fetchError.message : 'Network error';
      throw new Error(`Cannot connect to backend (${apiBaseUrl}): ${errorMsg}`, {
        cause: fetchError,
      });
    }

    if (!response.ok) {
      let errorMsg: string;
      try {
        const errorData = await response.json();
        errorMsg =
          errorData.detail ||
          errorData.message ||
          `Error ${response.status}: ${response.statusText}`;
      } catch {
        errorMsg = `Error ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMsg);
    }

    const data = await response.json();
    const { accessToken, refreshToken, user } = data;

    // Store tokens via adapter
    await adapter.setAccessToken(accessToken);
    await adapter.setRefreshToken(refreshToken);

    state = {
      user,
      accessToken,
      refreshToken,
      isLoading: false,
      isAuthenticated: true,
      error: null,
    };

    return { user, accessToken, refreshToken };
  }

  async function logout(): Promise<void> {
    const refreshToken = await adapter.getRefreshToken();
    if (refreshToken) {
      try {
        await fetch(`${apiBaseUrl}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
          signal: AbortSignal.timeout(5000),
        });
      } catch {
        // Clear the local session even when the identity service is unavailable.
      }
    }
    await adapter.setAccessToken(null);
    await adapter.setRefreshToken(null);

    state = {
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    };
  }

  async function refresh(): Promise<string | null> {
    const refreshToken = await adapter.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      await adapter.setAccessToken(data.accessToken);
      if (data.refreshToken) {
        await adapter.setRefreshToken(data.refreshToken);
      }

      state = {
        ...state,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken ?? state.refreshToken,
      };

      return data.accessToken;
    } catch {
      await logout();
      return null;
    }
  }

  async function refreshUser(): Promise<AuthUser> {
    const accessToken = await adapter.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${apiBaseUrl}/auth/status`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to refresh user state');
    }

    const data = await response.json();
    if (!data.authenticated || !data.user) {
      throw new Error('User is no longer authenticated');
    }

    state = { ...state, user: data.user };
    return data.user;
  }

  async function verifyToken(): Promise<void> {
    const accessToken = await adapter.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token');
    }

    const response = await fetch(`${apiBaseUrl}/auth/status`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Token verification failed');
    }
  }

  function getState(): AuthStateInternal {
    return { ...state };
  }

  return {
    login,
    logout,
    refresh,
    refreshUser,
    verifyToken,
    getState,
  };
}
